// /home/labdoo/zitech-nexus/frontend/src/pages/ClassMarksList.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './ClassMarksList.css';

// SVG Icons
const Icons = {
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  ChevronDown: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  ArrowRight: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  Check: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M20 6L9 17L4 12" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="cml-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 2V6M16 2V6M3 10H21" />
    </svg>
  ),
  User: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  School: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
};

interface ClassMarksRecord {
  id: number;
  school_name: string;
  class_display: string;
  term_display: string;
  exam_type_display: string;
  exam_year: number;
  total_students: number;
  status: string;
  status_display: string;
  created_at: string;
  user_name: string;
}

const ClassMarksList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<ClassMarksRecord[]>([]);
  const [filter, setFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'processed', label: 'Processed' },
    { value: 'published', label: 'Published' },
  ];

  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowStatusDropdown(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await api.get('/classmarks/records/');
      setRecords(response.data);
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      draft: '#6b7280',
      submitted: '#f59e0b',
      processed: '#0ea5e9',
      published: '#10b981',
    };
    return colors[status] || '#6b7280';
  };

  const getStatusBackground = (status: string): string => {
    const colors: Record<string, string> = {
      draft: '#f3f4f6',
      submitted: '#fef3c7',
      processed: '#dbeafe',
      published: '#d1fae5',
    };
    return colors[status] || '#f3f4f6';
  };

  const handleStatusChange = async (recordId: number, newStatus: string) => {
    setUpdatingStatus(recordId);
    try {
      await api.patch(`/classmarks/records/${recordId}/`, { status: newStatus });
      await fetchRecords();
      setShowStatusDropdown(null);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const toggleDropdown = (recordId: number) => {
    setShowStatusDropdown(showStatusDropdown === recordId ? null : recordId);
  };

  const filteredRecords = filter === 'all' 
    ? records 
    : records.filter(r => r.status === filter);

  const counts = {
    all: records.length,
    draft: records.filter(r => r.status === 'draft').length,
    submitted: records.filter(r => r.status === 'submitted').length,
    processed: records.filter(r => r.status === 'processed').length,
    published: records.filter(r => r.status === 'published').length,
  };

  if (loading) {
    return (
      <div className="cml-loading-container">
        <div className="cml-spinner">
          <Icons.Spinner />
        </div>
        <p className="cml-loading-text">Loading marks records...</p>
      </div>
    );
  }

  return (
    <div className="cml-container">
      {/* Header */}
      <div className="cml-header">
        <div className="cml-header-left">
          <h1 className="cml-title">Class Marks Records</h1>
          <p className="cml-subtitle">Manage and track student marks records</p>
        </div>
        <button 
          className="cml-btn-primary" 
          onClick={() => navigate('/dashboard/class-marks/create')}
        >
          <Icons.Plus />
          New Record
        </button>
      </div>

      {/* Stats Bar */}
      <div className="cml-stats-bar">
        <div className="cml-stat-item">
          <span className="cml-stat-label">Total</span>
          <span className="cml-stat-value">{counts.all}</span>
        </div>
        <div className="cml-stat-divider"></div>
        <div className="cml-stat-item">
          <span className="cml-stat-label">Draft</span>
          <span className="cml-stat-value cml-stat-draft">{counts.draft}</span>
        </div>
        <div className="cml-stat-divider"></div>
        <div className="cml-stat-item">
          <span className="cml-stat-label">Submitted</span>
          <span className="cml-stat-value cml-stat-submitted">{counts.submitted}</span>
        </div>
        <div className="cml-stat-divider"></div>
        <div className="cml-stat-item">
          <span className="cml-stat-label">Processed</span>
          <span className="cml-stat-value cml-stat-processed">{counts.processed}</span>
        </div>
        <div className="cml-stat-divider"></div>
        <div className="cml-stat-item">
          <span className="cml-stat-label">Published</span>
          <span className="cml-stat-value cml-stat-published">{counts.published}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="cml-filters">
        <button 
          className={`cml-filter-btn ${filter === 'all' ? 'cml-filter-active' : ''}`} 
          onClick={() => setFilter('all')}
        >
          All
          <span className="cml-filter-count">{counts.all}</span>
        </button>
        <button 
          className={`cml-filter-btn ${filter === 'draft' ? 'cml-filter-active' : ''}`} 
          onClick={() => setFilter('draft')}
        >
          Draft
          <span className="cml-filter-count">{counts.draft}</span>
        </button>
        <button 
          className={`cml-filter-btn ${filter === 'submitted' ? 'cml-filter-active' : ''}`} 
          onClick={() => setFilter('submitted')}
        >
          Submitted
          <span className="cml-filter-count">{counts.submitted}</span>
        </button>
        <button 
          className={`cml-filter-btn ${filter === 'processed' ? 'cml-filter-active' : ''}`} 
          onClick={() => setFilter('processed')}
        >
          Processed
          <span className="cml-filter-count">{counts.processed}</span>
        </button>
        <button 
          className={`cml-filter-btn ${filter === 'published' ? 'cml-filter-active' : ''}`} 
          onClick={() => setFilter('published')}
        >
          Published
          <span className="cml-filter-count">{counts.published}</span>
        </button>
      </div>

      {/* Table */}
      <div className="cml-table-wrapper">
        {filteredRecords.length === 0 ? (
          <div className="cml-empty-state">
            <Icons.Empty />
            <h3 className="cml-empty-title">No {filter !== 'all' ? filter : ''} Records Found</h3>
            <p className="cml-empty-text">
              {filter === 'all' ? 'Create your first marks record to get started.' : `You don't have any ${filter} marks records.`}
            </p>
            {filter === 'all' && (
              <button className="cml-btn-secondary" onClick={() => navigate('/dashboard/class-marks/create')}>
                <Icons.Plus />
                Create Your First Record
              </button>
            )}
          </div>
        ) : (
          <table className="cml-table">
            <thead>
              <tr>
                <th className="cml-col-id">ID</th>
                <th className="cml-col-school">School</th>
                <th className="cml-col-class">Class</th>
                <th className="cml-col-exam">Exam</th>
                <th className="cml-col-students">Students</th>
                <th className="cml-col-status">Status</th>
                <th className="cml-col-date">Submitted</th>
                <th className="cml-col-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((record) => (
                <tr key={record.id} className="cml-table-row">
                  <td className="cml-col-id">
                    <span className="cml-id-badge">#{record.id}</span>
                  </td>
                  <td className="cml-col-school">
                    <span className="cml-school-name">
                      <Icons.School />
                      {record.school_name}
                    </span>
                  </td>
                  <td className="cml-col-class">
                    <span className="cml-class-name">{record.class_display}</span>
                  </td>
                  <td className="cml-col-exam">
                    <span className="cml-exam-name">
                      {record.exam_type_display} {record.exam_year}
                    </span>
                  </td>
                  <td className="cml-col-students">
                    <span className="cml-students-count">
                      <Icons.User />
                      {record.total_students}
                    </span>
                  </td>
                  <td className="cml-col-status">
                    <div className="cml-status-wrapper" ref={dropdownRef}>
                      <span 
                        className="cml-status-badge"
                        style={{
                          background: getStatusBackground(record.status),
                          color: getStatusColor(record.status)
                        }}
                      >
                        <Icons.StatusDot />
                        {record.status_display}
                      </span>
                      <button
                        className="cml-status-update"
                        onClick={() => toggleDropdown(record.id)}
                        disabled={updatingStatus === record.id}
                        title="Update status"
                      >
                        <Icons.ChevronDown />
                      </button>
                      {showStatusDropdown === record.id && (
                        <>
                          <div className="cml-dropdown-overlay" onClick={() => setShowStatusDropdown(null)}></div>
                          <div className="cml-status-dropdown">
                            {statusOptions.map((option) => (
                              <button
                                key={option.value}
                                className={`cml-dropdown-item ${record.status === option.value ? 'cml-dropdown-active' : ''}`}
                                onClick={() => handleStatusChange(record.id, option.value)}
                                disabled={updatingStatus === record.id}
                              >
                                <span 
                                  className="cml-status-dot" 
                                  style={{ backgroundColor: getStatusColor(option.value) }}
                                />
                                {option.label}
                                {record.status === option.value && (
                                  <span className="cml-dropdown-check">
                                    <Icons.Check />
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="cml-col-date">
                    <span className="cml-date-text">
                      <Icons.Calendar />
                      {new Date(record.created_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </td>
                  <td className="cml-col-actions">
                    <button 
                      className="cml-action-btn"
                      onClick={() => navigate(`/dashboard/class-marks/${record.id}`)}
                    >
                      View Details
                      <Icons.ArrowRight />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ClassMarksList;